import axios, { AxiosPromise, AxiosResponse } from 'axios';
import { apiUrl } from '../configs/config';
import { msalConfig, apiRequest as msalApiRequest } from '../configs/authConfig';
import { PublicClientApplication } from '@azure/msal-browser';

// Create a singleton MSAL instance for API calls
// This is needed because we can't use React hooks in non-component files
const msalInstance = new PublicClientApplication(msalConfig);

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH'
}

const authorizationHeader = (token?: string): string => `Bearer ${token}`;

interface IHeaders {
  [header: string]: string;
}

// New interface for file download responses
export interface FileDownloadResponse {
  data: Blob | string | ArrayBuffer | Document;
  filename?: string;
  contentType?: string;
}

/**
 * Gets an access token for API calls
 * @returns A promise that resolves to an access token or null
 */
const getAccessToken = async (): Promise<string | null> => {
  // Initialize MSAL if needed
  await msalInstance.initialize();

  // Get active account
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    console.warn('No active account found for token acquisition');
    return null;
  }

  // Use the first account
  const account = accounts[0];

  try {
    // Try to acquire a token silently
    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...msalApiRequest,
      account
    });

    return tokenResponse.accessToken;
  } catch (error) {
    console.error('Error acquiring token silently:', error);

    // Don't trigger interactive acquisition here as it causes redirects
    // Just return null and let the consumer handle the authentication error
    return null;
  }
};

/**
 * Makes an authenticated API request
 * @param method HTTP method
 * @param resource API resource path
 * @param data Request payload
 * @param responseType Response type
 * @returns A promise with the API response
 */
export async function apiRequest<D, T>(
  method: HttpMethod,
  resource: string,
  data?: D,
  responseType: 'json' | 'blob' | 'text' | 'arraybuffer' | 'document' = 'json'
): Promise<T> {
  // Get an access token
  const token = await getAccessToken();

  if (!token) {
    throw new Error('No authentication token available');
  }

  const url = `${apiUrl}/api/${resource}`;
  const headers = new RequestHeaders();

  // Add content type for non-GET requests
  if (method !== HttpMethod.GET) {
    headers.addJsonContentType();
  }

  // Add authorization header
  headers.addAuthorizationHeader(token);

  try {
    // Make the API request
    const response = await axios({
      method,
      url,
      data,
      headers: headers.add(),
      responseType
    });

    // Handle file downloads for non-JSON responses
    if (responseType !== 'json') {
      const contentDisposition = response.headers['content-disposition'];

      if (contentDisposition) {
        // Try multiple patterns to extract filename
        let filename: string | undefined;

        // Pattern 1: filename="value" or filename='value'
        let match = contentDisposition.match(/filename[^;=\n]*=(["'])(.*?)\1/);
        if (match && match[2]) {
          filename = match[2];
        }

        // Pattern 2: filename=value (unquoted)
        if (!filename) {
          match = contentDisposition.match(/filename[^;=\n]*=([^;\n]*)/);
          if (match && match[1]) {
            filename = match[1].trim().replace(/^["']|["']$/g, '');
          }
        }

        // Pattern 3: filename*=UTF-8''value (RFC 5987)
        if (!filename) {
          match = contentDisposition.match(/filename\*=UTF-8''([^;\n]*)/);
          if (match && match[1]) {
            filename = decodeURIComponent(match[1]);
          }
        }

        if (filename) {
          const fileResponse: FileDownloadResponse = {
            data: response.data,
            filename,
            contentType: response.headers['content-type']?.toString()
          };
          return fileResponse as unknown as T;
        }
      }
    }

    return response.data;
  } catch (error) {
    // Handle API errors
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        // If unauthorized, clear token and throw a specific error
        console.error('Authentication error:', error);
        throw new Error('Authentication failed. Please log in again.');
      }
    }

    // Re-throw other errors
    throw error;
  }
}

/**
 * Makes an authenticated API request specifically for file downloads
 * @param method HTTP method
 * @param resource API resource path
 * @param data Request payload
 * @returns A promise with file download information
 */
export async function downloadFile<D>(
  method: HttpMethod,
  resource: string,
  data?: D
): Promise<FileDownloadResponse> {
  return apiRequest<D, FileDownloadResponse>(method, resource, data, 'blob');
}

export class RequestHeaders {
  private headers: IHeaders;

  constructor() {
    this.headers = {};
  }

  public addAuthorizationHeader(token: string) {
    this.headers = {
      ...this.headers,
      Authorization: authorizationHeader(token)
    };
    return this;
  }

  public addJsonContentType() {
    this.headers = {
      ...this.headers,
      'Content-Type': 'application/json'
    };
    return this;
  }

  public addNoCache() {
    this.headers = {
      ...this.headers,
      'Cache-Control': 'no-cache'
    };
    return this;
  }

  public add() {
    return this.headers;
  }
}